import { z } from 'zod';
export const trainingDocumentsRecordFactsCapability = 'training.documents-record-facts.v1';
export const trainingDocumentsRecordFactsSlot = 'documents.training-record-facts';
export const trainingDocumentsRecordFactsProviderId = 'training.server.documents-record-facts';
export const trainingDocumentsRecordKindSchema = z.enum([
    'transcript',
    'internal-completion-certificate',
]);
export const trainingDocumentsCourseSelectionSchema = z.strictObject({
    courseId: z.string().uuid(),
    releaseId: z.string().uuid(),
});
export const trainingDocumentsRecordFactsRequestSchema = z.strictObject({
    consumerModuleId: z.literal('documents'),
    requestedCapability: z.literal('documents.training-record-artifacts.v1'),
    resourceType: z.enum([
        'documents.training-record-artifact',
        'documents.training-record-options',
    ]),
    resourceId: z.string().uuid(),
    recordKind: trainingDocumentsRecordKindSchema,
    membershipId: z.string().uuid(),
    selectedCourse: trainingDocumentsCourseSelectionSchema.nullable(),
    includeProfessionalCertifications: z.boolean(),
    maximumInternalCompletions: z.number().int().min(1).max(500),
    maximumProfessionalCertifications: z.number().int().min(0).max(500),
    asOf: z.string().datetime({ offset: true }),
}).superRefine((value, context) => {
    if (value.recordKind === 'internal-completion-certificate') {
        if (value.selectedCourse === null) {
            context.addIssue({
                code: 'custom', path: ['selectedCourse'],
                message: 'An internal completion certificate requires one exact course release.',
            });
        }
        if (value.includeProfessionalCertifications) {
            context.addIssue({
                code: 'custom', path: ['includeProfessionalCertifications'],
                message: 'An internal completion certificate cannot include outside credentials.',
            });
        }
        if (value.maximumInternalCompletions !== 1) {
            context.addIssue({
                code: 'custom', path: ['maximumInternalCompletions'],
                message: 'An internal completion certificate resolves exactly one completion.',
            });
        }
    }
    else if (value.selectedCourse !== null) {
        context.addIssue({
            code: 'custom', path: ['selectedCourse'],
            message: 'A transcript includes the learner record rather than one selected course.',
        });
    }
    if (value.includeProfessionalCertifications !==
        (value.maximumProfessionalCertifications > 0)) {
        context.addIssue({
            code: 'custom', path: ['maximumProfessionalCertifications'],
            message: 'The outside-credential limit must match the requested transcript content.',
        });
    }
});
export const trainingDocumentsInternalAssessmentFactSchema = z.strictObject({
    attemptId: z.string().uuid(),
    attemptNumber: z.number().int().positive(),
    scoreBasisPoints: z.number().int().min(0).max(10_000),
    passed: z.literal(true),
    submittedAt: z.string().datetime({ offset: true }),
    assessmentSnapshotHash: z.string().regex(/^[a-f0-9]{64}$/),
});
export const trainingDocumentsInternalCompletionFactSchema = z.strictObject({
    courseId: z.string().uuid(),
    courseSlug: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
    releaseId: z.string().uuid(),
    releaseVersion: z.number().int().positive(),
    courseTitle: z.string().trim().min(1).max(250),
    courseOrigin: z.enum(['platform-release', 'tenant-release']),
    releaseContentHash: z.string().regex(/^[a-f0-9]{64}$/),
    completionState: z.literal('completed'),
    completionPercentage: z.literal(100),
    completedAt: z.string().datetime({ offset: true }),
    internalCompletionNotice: z.string().trim().min(20).max(500),
    assessment: trainingDocumentsInternalAssessmentFactSchema.nullable(),
    authorityBasis: z.literal('crest-tenant-internal-training-only'),
});
export const trainingDocumentsProfessionalCertificationFactSchema = z.strictObject({
    certificationId: z.string().uuid(),
    certificationName: z.string().trim().min(2).max(240),
    issuerName: z.string().trim().min(2).max(240),
    credentialNumber: z.string().trim().min(1).max(240).nullable(),
    issuedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    expiresOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
    lifecycleStatus: z.enum(['current', 'superseded', 'revoked']),
    statusAsOf: z.enum(['current', 'expired', 'non-expiring', 'superseded', 'revoked']),
    recordBasis: z.literal('tenant-attested'),
    issuerVerification: z.literal('not-verified-by-crest'),
});
export const trainingDocumentsRecordFactsPresentSchema = z.strictObject({
    state: z.literal('present'),
    capability: z.literal(trainingDocumentsRecordFactsCapability),
    tenantId: z.string().uuid(),
    membershipId: z.string().uuid(),
    recordKind: trainingDocumentsRecordKindSchema,
    asOf: z.string().datetime({ offset: true }),
    internalCompletionAuthority: z.literal('crest-tenant-internal-training-only'),
    professionalCertificationAuthority: z.literal('tenant-attested-not-verified-by-crest'),
    internalCompletions: z.array(trainingDocumentsInternalCompletionFactSchema).max(500),
    internalCompletionTotal: z.number().int().nonnegative().safe(),
    internalCompletionsTruncated: z.boolean(),
    professionalCertifications: z.array(trainingDocumentsProfessionalCertificationFactSchema).max(500),
    professionalCertificationTotal: z.number().int().nonnegative().safe(),
    professionalCertificationsTruncated: z.boolean(),
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
}).superRefine((value, context) => {
    if (value.internalCompletionTotal < value.internalCompletions.length ||
        value.internalCompletionsTruncated !==
            (value.internalCompletionTotal > value.internalCompletions.length)) {
        context.addIssue({
            code: 'custom', path: ['internalCompletionTotal'],
            message: 'Internal completion totals must reconcile with the returned facts.',
        });
    }
    if (value.professionalCertificationTotal < value.professionalCertifications.length ||
        value.professionalCertificationsTruncated !==
            (value.professionalCertificationTotal > value.professionalCertifications.length)) {
        context.addIssue({
            code: 'custom', path: ['professionalCertificationTotal'],
            message: 'Professional credential totals must reconcile with the returned facts.',
        });
    }
    if (value.recordKind === 'internal-completion-certificate' &&
        (value.internalCompletions.length !== 1 ||
            value.internalCompletionTotal !== 1 ||
            value.professionalCertifications.length !== 0 ||
            value.professionalCertificationTotal !== 0)) {
        context.addIssue({
            code: 'custom', path: ['recordKind'],
            message: 'An internal completion certificate contains exactly one internal completion.',
        });
    }
});
export const trainingDocumentsRecordFactsNotFoundSchema = z.strictObject({
    state: z.literal('not-found'),
    capability: z.literal(trainingDocumentsRecordFactsCapability),
    tenantId: z.string().uuid(),
    membershipId: z.string().uuid(),
    recordKind: z.literal('internal-completion-certificate'),
    asOf: z.string().datetime({ offset: true }),
    reason: z.literal('internal-completion-not-found'),
});
export const trainingDocumentsRecordFactsResponseSchema = z.discriminatedUnion('state', [
    trainingDocumentsRecordFactsPresentSchema,
    trainingDocumentsRecordFactsNotFoundSchema,
]);
//# sourceMappingURL=training-documents-record-facts.contract.js.map